import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { WaitingPart } from "@dentacore/shared-types";

import { listWaiting } from "../api/plans";
import { useActiveBranch } from "../api/use-active-branch";
import { HelpTip } from "../ui/popovers";
import styles from "./plans.module.css";

/** How many to draw before the rest wait behind "Show all". */
const SHOWN = 5;

/**
 * What is still to book — STORY-082b, moved to Home 2026-09-11, and since
 * PV5 (2026-09-24) the visits of multi-visit procedures, each one click from
 * the booking form.
 *
 * ── Why it is on Home ──────────────────────────────────────────────────────
 *
 * **A worklist has to find the person, not wait to be found.** The whole case
 * for this list is that a prompt somebody dismissed leaves a prepped crown with
 * no cementation date and nothing anywhere saying so. On Home the front desk
 * meets it every morning.
 *
 * ── One row that opens, since CS2 ──────────────────────────────────────────
 *
 * It sits in Home's *Needs you* tab as a single line — how many visits wait
 * and how many of them are stalled — and opens in place to the same list with
 * the same links. On a busy practice it ran to twenty-eight rows at the top of
 * the page; the count still finds the person, and the list is one click away.
 *
 * ── It draws nothing when there is nothing ─────────────────────────────────
 *
 * No "all clear" row. `onCount` tells the tab how many there are, so *Needs
 * you* can say "nothing needs you" when this and everything beside it is empty.
 */
export function WaitingBand({
  onCount,
}: {
  /** How many visits are waiting, once known. */
  onCount?: (count: number) => void;
}): React.JSX.Element | null {
  const branchId = useActiveBranch();
  const [rows, setRows] = useState<readonly WaitingPart[]>([]);
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (branchId === null) return;

    const controller = new AbortController();

    listWaiting(controller.signal)
      .then((loaded) => {
        setRows(loaded);
        onCount?.(loaded.length);
      })
      .catch(() => {
        /*
         * **Silent, and deliberately so.** Home already surfaces its own
         * failures in one place; a second error line for a list that is usually
         * empty would put a red row on a screen that is otherwise fine.
         */
      });

    return () => controller.abort();
  }, [branchId, onCount]);

  if (rows.length === 0) return null;

  const stalled = rows.filter((row) => row.reason === "STALLED").length;
  // Done ahead of its turn (PP-D9) is not "to book" — counted beside it.
  const ahead = rows.filter((row) => row.reason === "AHEAD").length;
  const toBook = rows.length - ahead;
  const shown = all ? rows : rows.slice(0, SHOWN);
  const rest = rows.length - shown.length;

  return (
    <section className={styles.waitingBand} data-testid="waiting-band">
      <div className={styles.waitingHead}>
        <span className={styles.waitingDot} aria-hidden="true" />
        <h2 className={styles.waitingTitle}>
          {toBook === 0
            ? ahead === 1
              ? "1 visit done out of turn"
              : `${String(ahead)} visits done out of turn`
            : toBook === 1
              ? "1 visit still to book"
              : `${String(toBook)} visits still to book`}
        </h2>
        <HelpTip label="About visits still to book">
          Visits of a procedure with no date yet, or whose booking fell through. Choose one to book
          it.
        </HelpTip>
        {stalled > 0 && <span className={styles.waitingNote}>{stalled} stalled</span>}
        {ahead > 0 && toBook > 0 && (
          <span className={styles.waitingNote}>{ahead} done out of turn</span>
        )}
        <button
          type="button"
          className={styles.waitingToggle}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          data-testid="waiting-toggle"
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <>
          <ul className={styles.waitingList}>
            {shown.map((row) => (
              <li key={row.partId} className={styles.waitingItem}>
                {/*
                  **Straight to the booking form, on this visit** (PV5): the
                  patient chosen and the visit picked, with any later visits
                  proposed under it.
                */}
                <Link
                  to={
                    // A visit done ahead needs the earlier one marked done, on
                    // the patient's Treatment tab — not another booking (PP-D9).
                    row.reason === "AHEAD"
                      ? `/patients/${row.patientId}?tab=Treatment`
                      : `/calendar?patient=${row.patientId}&visit=${row.planId}:${row.partId}`
                  }
                  className={styles.waitingLink}
                  data-testid="waiting-row"
                >
                  <span className={styles.waitingWho}>{row.patientName}</span>
                  <span className={styles.waitingWhat}>
                    {row.planTitle ?? "Treatment"}
                    {row.partTitle !== null && ` — ${row.partTitle}`}
                    {/*
                      **Stalled reads differently from ready, because the action
                      is different**: ready means book it, stalled means the visit
                      in front of it was booked and then cancelled or missed, so
                      that one has to be rebooked before this can go in.
                    */}
                    {row.reason === "AHEAD" && row.doneAheadVisit !== null && (
                      <span className={styles.stalledTag} data-testid="waiting-ahead">
                        visit {row.doneAheadVisit} done first — mark visit {row.visitNumber} done
                      </span>
                    )}
                    {row.reason === "STALLED" && (
                      <span className={styles.stalledTag} data-testid="waiting-stalled">
                        stalled
                      </span>
                    )}
                  </span>
                  <span className={styles.waitingVisit}>
                    visit {row.visitNumber} of {row.visitCount}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {rest > 0 && (
            <p className={styles.waitingMore}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setAll(true)}
                data-testid="waiting-show-all"
              >
                Show all {rows.length}
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}
